var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StatusPermission } from '../../enum';
import { RumEntityLabel } from '../../observability/rumEntity';
import { ROUTER_URL } from '../../router/constants';
import { formatNumberByLocale } from '../../util/locale';
import { useOrgConfigPermission } from '../app/selectors';
import { Autocomplete, ColumnFilterHeader, ConfirmDialog, DataGrid, FallbackComponent, useClientTableFilter, useCustomizableColumns, } from '../common';
import { PageBody, SubTitle } from '../layout/styled';
import { useClientsAPI } from '../orgConfigWizard/clients/selectors';
import { useSeatAllocationsAPI } from '../orgConfigWizard/seats/selectors';
import { useCurrentVersionAPI, useLocationsAPI, useOrgConfigVersionAPI } from '../orgConfigWizard/selectors';
var DismissibleAlert = function (_a) {
    var onClose = _a.onClose, sx = _a.sx, children = _a.children, props = __rest(_a, ["onClose", "sx", "children"]);
    return (_jsx(Alert, __assign({}, props, { action: _jsx(IconButton, __assign({ color: "inherit", size: "small", onClick: onClose, sx: { px: '8px' } }, { children: _jsx(FontAwesomeIcon, { icon: "xmark" }) })), sx: __assign({ border: '1px solid', borderColor: 'success.main' }, sx) }, { children: children })));
};
// A cell whose text is a client-defined name. Uses the wrapper and tooltip MUI gives a plain cell, so long
// names still truncate and show in full on hover.
var NameCell = function (_a) {
    var title = _a.title, children = _a.children;
    return (_jsx("div", __assign({ className: gridClasses.cellContent, title: title !== null && title !== void 0 ? title : undefined }, { children: children })));
};
var ORG_LOCATION_TABLE_KEY = 'myOrgLocationTable';
var ORG_LOCATION_DEFAULT_FIELDS = [
    'locationName',
    'city',
    'stateProvince',
    'countryName',
    'totalSeats',
    'seatsReserved',
];
var ORG_CLIENT_TABLE_KEY = 'myOrgClientTable';
var ORG_CLIENT_DEFAULT_FIELDS = [
    'clientName',
    'businessUnit',
    'lobName',
    'locationName',
    'region',
    'allocatedSeats',
    'updatedAt',
];
// Business Unit and Line of Business are available in the customizer but hidden by default.
var ORG_CLIENT_DEFAULT_HIDDEN = ['businessUnit', 'lobName'];
var formatDate = function (iso) {
    var d = new Date(iso);
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var h = d.getHours();
    var mins = String(d.getMinutes()).padStart(2, '0');
    var ampm = h >= 12 ? 'PM' : 'AM';
    return "".concat(months[d.getMonth()], " ").concat(d.getDate(), " ").concat(d.getFullYear(), " ").concat(h % 12 || 12, ":").concat(mins).concat(ampm);
};
export var MyOrganization = function () {
    var _a, _b;
    var t = useTranslation('orgConfig', { keyPrefix: 'organization' }).t;
    var navigate = useNavigate();
    var _c = useSearchParams(), searchParams = _c[0], setSearchParams = _c[1];
    var _d = useState('location'), viewMode = _d[0], setViewMode = _d[1];
    var _e = useState(searchParams.get('configured') === '1'), showConfiguredBanner = _e[0], setShowConfiguredBanner = _e[1];
    var _f = useState(false), showReconfigureWarning = _f[0], setShowReconfigureWarning = _f[1];
    var orgConfigPermission = useOrgConfigPermission();
    var isViewOnly = orgConfigPermission === StatusPermission.View;
    useEffect(function () {
        if (searchParams.get('configured') === '1') {
            var next = new URLSearchParams(searchParams);
            next.delete('configured');
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var _g = useOrgConfigVersionAPI(), ensureVersionData = _g[0], fetchEnsureVersion = _g[1];
    var _h = useCurrentVersionAPI(), currentVersionData = _h[0], fetchCurrentVersion = _h[1];
    var versionData = isViewOnly ? currentVersionData : ensureVersionData;
    useEffect(function () {
        if (isViewOnly)
            fetchCurrentVersion();
        else
            fetchEnsureVersion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var configVersionId = (_b = (_a = versionData.data) === null || _a === void 0 ? void 0 : _a.configVersionId) !== null && _b !== void 0 ? _b : null;
    var _j = useLocationsAPI(configVersionId), locationsData = _j[0], fetchLocations = _j[1];
    var _k = useSeatAllocationsAPI(configVersionId), allocationsData = _k[0], fetchAllocations = _k[1];
    var _l = useClientsAPI(configVersionId), clientsData = _l[0], fetchClients = _l[1];
    var isRequesting = locationsData.requesting || allocationsData.requesting || clientsData.requesting;
    useEffect(function () {
        if (configVersionId) {
            fetchLocations();
            fetchAllocations();
            fetchClients();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configVersionId]);
    var locations = useMemo(function () { var _a, _b; return (_b = (_a = locationsData.data) === null || _a === void 0 ? void 0 : _a.locations) !== null && _b !== void 0 ? _b : []; }, [locationsData.data]);
    var allocations = useMemo(function () { var _a; return (_a = allocationsData.data) !== null && _a !== void 0 ? _a : []; }, [allocationsData.data]);
    var clients = useMemo(function () {
        var _a;
        var data = (_a = clientsData.data) !== null && _a !== void 0 ? _a : [];
        return __spreadArray([], data, true).sort(function (a, b) { return (b.isHqClient ? 1 : 0) - (a.isHqClient ? 1 : 0); });
    }, [clientsData.data]);
    var locationMap = useMemo(function () { return new Map(locations.map(function (l) { return [l.id, l]; })); }, [locations]);
    var reservedByLocation = useMemo(function () {
        var _a;
        var map = new Map();
        for (var _i = 0, allocations_1 = allocations; _i < allocations_1.length; _i++) {
            var a = allocations_1[_i];
            map.set(a.locationId, ((_a = map.get(a.locationId)) !== null && _a !== void 0 ? _a : 0) + a.allocatedSeats);
        }
        return map;
    }, [allocations]);
    var locationRows = useMemo(function () {
        return locations.map(function (loc) {
            var _a;
            return (__assign(__assign({}, loc), { seatsReserved: loc.totalSeats == null ? null : (_a = reservedByLocation.get(loc.id)) !== null && _a !== void 0 ? _a : 0 }));
        });
    }, [locations, reservedByLocation]);
    var clientsById = useMemo(function () { return new Map(clients.map(function (c) { return [c.id, c]; })); }, [clients]);
    var clientRows = useMemo(function () {
        var allocationRows = allocations.map(function (a) {
            var _a, _b, _c, _d;
            return ({
                id: String(a.id),
                clientId: a.clientId,
                clientName: a.clientName,
                buId: a.buId,
                businessUnit: a.buName,
                lobId: a.lobId,
                subLobId: a.subLobId,
                // `lobName` is the combined "LOB: Sub-LOB" label; these are its two parts.
                lobOwnName: a.lobName,
                subLobName: a.subLobName,
                lobName: a.subLobName ? "".concat(a.lobName, ": ").concat(a.subLobName) : a.lobName,
                locationId: a.locationId,
                locationName: a.locationName,
                region: (_b = (_a = locationMap.get(a.locationId)) === null || _a === void 0 ? void 0 : _a.region) !== null && _b !== void 0 ? _b : null,
                allocatedSeats: a.allocatedSeats,
                updatedAt: a.updatedAt,
                isHqClient: (_d = (_c = clientsById.get(a.clientId)) === null || _c === void 0 ? void 0 : _c.isHqClient) !== null && _d !== void 0 ? _d : false,
            });
        });
        var clientsWithAllocations = new Set(allocations.map(function (a) { return a.clientId; }));
        var noAllocRows = clients
            .filter(function (c) { return !clientsWithAllocations.has(c.id); })
            .map(function (c) { return ({
            id: "client-".concat(c.id),
            clientId: c.id,
            clientName: c.clientName,
            buId: null,
            businessUnit: null,
            lobId: null,
            subLobId: null,
            lobOwnName: null,
            subLobName: null,
            lobName: null,
            locationId: null,
            locationName: null,
            region: null,
            allocatedSeats: null,
            updatedAt: c.updatedAt,
            isHqClient: c.isHqClient,
        }); });
        return __spreadArray(__spreadArray([], allocationRows, true), noAllocRows, true);
    }, [allocations, clients, clientsById, locationMap]);
    var locationFilterColumns = useMemo(function () { return [
        { field: 'locationName', filter: 'text', sortable: true },
        { field: 'city', filter: 'text', sortable: true },
        { field: 'stateProvince', filter: 'text', sortable: true },
        { field: 'countryName', filter: 'enum', sortable: true },
        { field: 'totalSeats', sortable: true },
        { field: 'seatsReserved', sortable: true },
    ]; }, []);
    var clientFilterColumns = useMemo(function () { return [
        { field: 'clientName', filter: 'text', sortable: true },
        { field: 'businessUnit', filter: 'text', sortable: true },
        { field: 'lobName', filter: 'text', sortable: true },
        { field: 'locationName', filter: 'text', sortable: true },
        { field: 'region', filter: 'enum', sortable: true },
        { field: 'allocatedSeats', sortable: true },
        { field: 'updatedAt', filter: 'date', sortable: true },
    ]; }, []);
    var _m = useClientTableFilter({ rows: locationRows, columns: locationFilterColumns }), processedLocationRows = _m.processedRows, getLocationFilterState = _m.getFilterState, getLocationAllValues = _m.getAllValues, applyLocationFilter = _m.applyFilter;
    var _o = useClientTableFilter({ rows: clientRows, columns: clientFilterColumns }), processedClientRows = _o.processedRows, getClientFilterState = _o.getFilterState, getClientAllValues = _o.getAllValues, applyClientFilter = _o.applyFilter;
    var sortedClientRows = useMemo(function () {
        var hq = processedClientRows.filter(function (r) { return r.isHqClient; });
        var rest = processedClientRows.filter(function (r) { return !r.isHqClient; });
        return __spreadArray(__spreadArray([], hq, true), rest, true);
    }, [processedClientRows]);
    var makeLocationHeader = useCallback(function (field, label) { return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getLocationFilterState(field), allValues: getLocationAllValues(field), onApply: function (update) { return applyLocationFilter(field, update); } })); }; }, [applyLocationFilter, getLocationAllValues, getLocationFilterState]);
    var makeClientHeader = useCallback(function (field, label) { return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getClientFilterState(field), allValues: getClientAllValues(field), onApply: function (update) { return applyClientFilter(field, update); } })); }; }, [applyClientFilter, getClientAllValues, getClientFilterState]);
    var locationColumns = useMemo(function () { return [
        {
            field: 'locationName',
            headerName: t('table.siteLocationName'),
            flex: 2,
            sortable: false,
            renderHeader: makeLocationHeader('locationName', t('table.siteLocationName')),
            renderCell: function (params) { return (_jsx(NameCell, __assign({ title: params.formattedValue }, { children: _jsx(RumEntityLabel, __assign({ kind: "location", id: params.row.id }, { children: params.formattedValue })) }))); },
        },
        {
            field: 'city',
            headerName: t('table.cityTown'),
            flex: 1,
            sortable: false,
            renderHeader: makeLocationHeader('city', t('table.cityTown')),
        },
        {
            field: 'stateProvince',
            headerName: t('table.provState'),
            flex: 1,
            sortable: false,
            renderHeader: makeLocationHeader('stateProvince', t('table.provState')),
        },
        {
            field: 'countryName',
            headerName: t('table.country'),
            flex: 1,
            sortable: false,
            renderHeader: makeLocationHeader('countryName', t('table.country')),
        },
        {
            field: 'totalSeats',
            headerName: t('table.totalSeats'),
            flex: 1,
            sortable: false,
            renderHeader: makeLocationHeader('totalSeats', t('table.totalSeats')),
            valueFormatter: function (_a) {
                var value = _a.value;
                return (value == null ? t('table.unlimited') : formatNumberByLocale(value));
            },
        },
        {
            field: 'seatsReserved',
            headerName: t('table.seatsReserved'),
            flex: 1,
            sortable: false,
            renderHeader: makeLocationHeader('seatsReserved', t('table.seatsReserved')),
            valueFormatter: function (_a) {
                var value = _a.value;
                return (value == null ? t('table.unlimited') : formatNumberByLocale(value));
            },
        },
    ]; }, [t, makeLocationHeader]);
    var clientColumns = useMemo(function () { return [
        {
            field: 'clientName',
            headerName: t('table.companyClient'),
            flex: 2,
            sortable: false,
            renderHeader: makeClientHeader('clientName', t('table.companyClient')),
            renderCell: function (params) { return (_jsx(NameCell, __assign({ title: params.formattedValue }, { children: _jsx(RumEntityLabel, __assign({ kind: "client", id: params.row.clientId }, { children: params.formattedValue })) }))); },
        },
        {
            field: 'businessUnit',
            headerName: t('table.businessUnit'),
            flex: 2,
            sortable: false,
            renderHeader: makeClientHeader('businessUnit', t('table.businessUnit')),
            valueFormatter: function (_a) {
                var value = _a.value;
                return value !== null && value !== void 0 ? value : '-';
            },
            renderCell: function (params) { return (_jsx(NameCell, __assign({ title: params.formattedValue }, { children: _jsx(RumEntityLabel, __assign({ kind: "businessUnit", id: params.row.buId }, { children: params.formattedValue })) }))); },
        },
        {
            field: 'lobName',
            headerName: t('table.lineOfBusiness'),
            flex: 2,
            sortable: false,
            renderHeader: makeClientHeader('lobName', t('table.lineOfBusiness')),
            valueFormatter: function (_a) {
                var value = _a.value;
                return value !== null && value !== void 0 ? value : '-';
            },
            // Same text as the formatted value ("LOB" or "LOB: Sub-LOB"), with each name wrapped on its own.
            renderCell: function (params) { return (_jsx(NameCell, __assign({ title: params.formattedValue }, { children: params.row.lobOwnName == null ? (params.formattedValue) : (_jsxs(_Fragment, { children: [_jsx(RumEntityLabel, __assign({ kind: "lob", id: params.row.lobId }, { children: params.row.lobOwnName })), params.row.subLobName && (_jsxs(_Fragment, { children: [': ', _jsx(RumEntityLabel, __assign({ kind: "subLob", id: params.row.subLobId }, { children: params.row.subLobName }))] }))] })) }))); },
        },
        {
            field: 'locationName',
            headerName: t('table.siteLocationName'),
            flex: 1.5,
            sortable: false,
            renderHeader: makeClientHeader('locationName', t('table.siteLocationName')),
            valueFormatter: function (_a) {
                var value = _a.value;
                return value !== null && value !== void 0 ? value : '-';
            },
            renderCell: function (params) { return (_jsx(NameCell, __assign({ title: params.formattedValue }, { children: _jsx(RumEntityLabel, __assign({ kind: "location", id: params.row.locationId }, { children: params.formattedValue })) }))); },
        },
        {
            field: 'region',
            headerName: t('table.region'),
            flex: 1,
            sortable: false,
            renderHeader: makeClientHeader('region', t('table.region')),
            valueFormatter: function (_a) {
                var value = _a.value;
                return value !== null && value !== void 0 ? value : '-';
            },
        },
        {
            field: 'allocatedSeats',
            headerName: t('table.seatsReserved'),
            flex: 1,
            sortable: false,
            renderHeader: makeClientHeader('allocatedSeats', t('table.seatsReserved')),
            valueFormatter: function (_a) {
                var value = _a.value;
                return (value == null ? '-' : formatNumberByLocale(value));
            },
        },
        {
            field: 'updatedAt',
            headerName: t('table.lastEdited'),
            flex: 1.5,
            sortable: false,
            renderHeader: makeClientHeader('updatedAt', t('table.lastEdited')),
            valueFormatter: function (_a) {
                var value = _a.value;
                return (value ? formatDate(value) : '-');
            },
        },
    ]; }, [t, makeClientHeader]);
    var customizeLabel = t('customizeColumns.tooltip', { defaultValue: 'Customize columns' });
    var resetLabel = t('customizeColumns.reset', { defaultValue: 'Reset' });
    var locationGridColumns = useCustomizableColumns({
        tableKey: ORG_LOCATION_TABLE_KEY,
        defaultFields: ORG_LOCATION_DEFAULT_FIELDS,
        lockedField: 'locationName',
        columns: locationColumns,
        customizeLabel: customizeLabel,
        resetLabel: resetLabel,
    });
    var clientGridColumns = useCustomizableColumns({
        tableKey: ORG_CLIENT_TABLE_KEY,
        defaultFields: ORG_CLIENT_DEFAULT_FIELDS,
        defaultHidden: ORG_CLIENT_DEFAULT_HIDDEN,
        lockedField: 'clientName',
        columns: clientColumns,
        customizeLabel: customizeLabel,
        resetLabel: resetLabel,
    });
    // "Configured" means the org actually has location data — not merely that a
    // finalized version row exists. Org creation seeds a finalized version 1
    // (finalized_at = NOW()), so isPublished/versionNumber are true even for a
    // brand-new org that has never been configured. Gating on real data avoids
    // showing the re-configure warning to first-time users.
    var hasData = locations.length > 0;
    var tableContent;
    if (!hasData) {
        tableContent = (_jsx(Box, __assign({ sx: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover',
            } }, { children: t('noData') })));
    }
    else if (viewMode === 'location') {
        tableContent = (_jsx(DataGrid, { rows: processedLocationRows, columns: locationGridColumns, getRowId: function (row) { return row.id; }, autoHeight: true, hideFooter: true, rowHeight: 40 }));
    }
    else {
        tableContent = (_jsx(DataGrid, { rows: sortedClientRows, columns: clientGridColumns, getRowId: function (row) { return row.id; }, autoHeight: true, hideFooter: true, rowHeight: 40 }));
    }
    return (_jsxs(PageBody, { children: [showConfiguredBanner && (_jsx(DismissibleAlert, __assign({ severity: "success", onClose: function () { return setShowConfiguredBanner(false); }, sx: { mb: 4 } }, { children: t('configured') }))), _jsxs(Typography, __assign({ variant: "h1", sx: { mb: 2 } }, { children: [_jsx(FontAwesomeIcon, { icon: "location-dot" }), "\u00A0\u00A0", t('myOrganization')] })), _jsxs(Box, __assign({ sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                } }, { children: [_jsx(SubTitle, __assign({ sx: { mb: 0 } }, { children: t('subtitle') })), !hasData && !isViewOnly && (_jsx(Button, __assign({ variant: "outlined", onClick: function () { return navigate(ROUTER_URL.OrganizationWizard); } }, { children: t('configureOrganization') })))] })), hasData && (_jsxs(Box, __assign({ sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                } }, { children: [_jsx(Autocomplete, { options: [
                            { value: 'location', label: t('viewByLocation') },
                            { value: 'client', label: t('viewByClient') },
                        ], getOptionValue: function (o) { return o.value; }, getOptionLabel: function (o) { return o.label; }, value: viewMode, onChange: function (value) { return setViewMode(value); }, sx: { maxWidth: '16rem' } }), !isViewOnly && (_jsx(Button, __assign({ variant: "outlined", onClick: function () { return setShowReconfigureWarning(true); } }, { children: t('configureOrganization') })))] }))), _jsx(FallbackComponent, __assign({ requesting: isRequesting, overlay: true }, { children: tableContent })), _jsx(ConfirmDialog, { open: showReconfigureWarning, onClose: function () { return setShowReconfigureWarning(false); }, title: t('reconfigureWarning.title', { defaultValue: 'Warning' }), confirmButtonText: "continue", onConfirm: function () {
                    setShowReconfigureWarning(false);
                    navigate(ROUTER_URL.OrganizationWizard);
                }, message: _jsx(Trans, { t: t, i18nKey: "reconfigureWarning.message", components: { strong: _jsx("strong", {}) }, defaults: "Re-configuring your organization will allow you to add, remove or edit any of your organizational data. Any <strong>new</strong> plans that are created will use the new configuration. Queues may also be affected for any new plans created." }) })] }));
};
